import React, { useEffect, useState } from 'react';
import { Search, Plus, Edit2, Trash2, UserPlus, Truck, Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import OwnerSelect from '../components/OwnerSelect';
import useUserPreference from '../hooks/useUserPreference';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

const PHONE_TYPES = ['jefe', 'cliente', 'proveedor', 'contacto', 'familia', 'incidencia'];

const PHONE_TYPE_BADGE = {
  jefe: 'badge-warning',
  cliente: 'badge-success',
  proveedor: 'badge-info',
  contacto: 'badge-gray',
  familia: 'badge-info',
  incidencia: 'badge-danger',
};

const emptyContact = {
  contactName: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
  companyName: '',
  phoneType: 'contacto',
  notes: '',
  specialInstructions: '',
  ownerId: null,
};

const Phonebook = () => {
  const { hasPermission } = useAuth();
  const modal = useModal();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useUserPreference('phonebook:search', '');
  const [typeFilter, setTypeFilter] = useUserPreference('phonebook:type', '');
  const [viewMode, setViewMode] = useViewMode('phonebook');
  const fl = useFieldLayout('phonebook', viewMode);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyContact });
  const [contactToDelete, setContactToDelete] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null); // {contact, target:'customer'|'provider'}

  useEffect(() => { loadContacts(); }, []);

  const loadContacts = async () => {
    try {
      const res = await api.get('/phonebook');
      setContacts(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      modal.error('Error cargando agenda');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contactName.trim()) {
      modal.error('El nombre es obligatorio');
      return;
    }
    try {
      if (editing) {
        await api.put(`/phonebook/${editing}`, form);
      } else {
        await api.post('/phonebook', form);
      }
      await loadContacts();
      closeModal();
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error guardando contacto');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/phonebook/${id}`);
      await loadContacts();
      setContactToDelete(null);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error borrando contacto');
    }
  };

  const handleConvert = async () => {
    if (!convertTarget) return;
    const { contact, target } = convertTarget;
    const path = target === 'customer' ? 'convert-to-customer' : 'convert-to-provider';
    try {
      const res = await api.post(`/phonebook/${contact.id}/${path}`);
      const newId = target === 'customer' ? res.data?.customer?.id : res.data?.provider?.id;
      const label = target === 'customer' ? 'Cliente' : 'Proveedor';
      await loadContacts();
      modal.success(`${label} creado (id ${newId}). Edítalo para completar datos.`);
      setConvertTarget(null);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error en la conversión');
    }
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({
      contactName: c.contactName || '',
      firstName: c.firstName || '',
      lastName: c.lastName || '',
      phoneNumber: c.phoneNumber || '',
      email: c.email || '',
      companyName: c.companyName || '',
      phoneType: c.phoneType || 'contacto',
      notes: c.notes || '',
      specialInstructions: c.specialInstructions || '',
      ownerId: c.ownerId ?? null,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm({ ...emptyContact });
  };

  const filtered = contacts.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (c.contactName || '').toLowerCase().includes(q) ||
      (c.phoneNumber || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.companyName || '').toLowerCase().includes(q);
    const matchType = !typeFilter || c.phoneType === typeFilter;
    return matchSearch && matchType;
  });

  const columns = [
    { key: 'contactName', header: 'Nombre', className: 'dt-strong', fixed: true, render: (c) => (
      <div>
        {c.contactName}
        {c.companyName && <div className="dt-sub" style={{ fontWeight: 400 }}>{c.companyName}</div>}
      </div>
    ) },
    { key: 'phoneType', header: 'Tipo', render: (c) => (
      <span className={`badge ${PHONE_TYPE_BADGE[c.phoneType] || 'badge-gray'}`}>{c.phoneType}</span>
    ) },
    { key: 'phoneNumber', header: 'Teléfono', className: 'dt-nowrap', render: (c) => c.phoneNumber || '—' },
    { key: 'email', header: 'Email', render: (c) => c.email || '—' },
    { key: 'customer', header: 'Cliente', render: (c) => (c.customer ? `#${c.customer.id} · ${c.customer.name}` : '—') },
    { key: 'provider', header: 'Proveedor', render: (c) => (c.provider ? `#${c.provider.id} · ${c.provider.name}` : '—') },
    { key: 'actions', header: '', className: 'col-actions', render: (c) => (
      <div className="dt-actions">
        <button className="btn-icon" onClick={() => openEdit(c)} title="Editar">
          <Edit2 size={16} />
        </button>
        {!c.customer && (
          <button className="btn-icon" onClick={() => setConvertTarget({ contact: c, target: 'customer' })}
            title="Convertir en cliente" style={{ color: '#16a34a' }}>
            <UserPlus size={16} />
          </button>
        )}
        {!c.provider && (
          <button className="btn-icon" onClick={() => setConvertTarget({ contact: c, target: 'provider' })}
            title="Convertir en proveedor" style={{ color: '#1d4ed8' }}>
            <Truck size={16} />
          </button>
        )}
        <button className="btn-icon" onClick={() => setContactToDelete(c.id)} title="Borrar" style={{ color: '#ef4444' }}>
          <Trash2 size={16} />
        </button>
      </div>
    ) },
  ];

  const fields = fl.fields(columns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Agenda</h1>
          <p className="page-subtitle">{contacts.length} contactos registrados</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={18} /> Nuevo contacto
        </button>
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <Search />
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por nombre, teléfono, email o empresa..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select className="form-input" style={{ width: 200 }} value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}>
            <option value="">Todos los tipos</option>
            {PHONE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <div style={{ marginLeft: 'auto' }}>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={fields}
              hidden={fl.hidden}
              onToggleField={fl.toggle}
              onReorderFields={fl.reorder}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <Phone size={64} />
            <h3>No hay contactos</h3>
            <p>Añade un contacto a la agenda para empezar</p>
          </div>
        ) : viewMode === 'table' ? (
          <DataTable columns={fl.orderColumns(columns)} rows={filtered} hiddenKeys={fl.hidden} />
        ) : (
          <div className="items-grid">
            {filtered.map(contact => (
              <div className="item-card" key={contact.id}>
                <div className="item-card-header">
                  <div className="item-card-avatar">{(contact.contactName || '?').charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="item-card-title">{contact.contactName}</div>
                    {contact.companyName && <div className="item-card-subtitle">{contact.companyName}</div>}
                  </div>
                  {!fl.isHidden('phoneType') && (
                    <span className={`badge ${PHONE_TYPE_BADGE[contact.phoneType] || 'badge-gray'}`}>
                      {contact.phoneType}
                    </span>
                  )}
                </div>
                <div className="item-card-body">
                  {!fl.isHidden('phoneNumber') && contact.phoneNumber && (
                    <div className="item-card-row" style={fl.cardStyle('phoneNumber')}>
                      <span className="item-label">Tel</span>
                      <span>{contact.phoneNumber}</span>
                    </div>
                  )}
                  {!fl.isHidden('email') && contact.email && (
                    <div className="item-card-row" style={fl.cardStyle('email')}>
                      <span className="item-label">Email</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.email}</span>
                    </div>
                  )}
                  {!fl.isHidden('customer') && contact.customer && (
                    <div className="item-card-row" style={fl.cardStyle('customer')}>
                      <span className="item-label">Cliente</span>
                      <span>#{contact.customer.id} · {contact.customer.name}</span>
                    </div>
                  )}
                  {!fl.isHidden('provider') && contact.provider && (
                    <div className="item-card-row" style={fl.cardStyle('provider')}>
                      <span className="item-label">Proveedor</span>
                      <span>#{contact.provider.id} · {contact.provider.name}</span>
                    </div>
                  )}
                </div>
                <div className="item-card-footer">
                  <div className="item-card-actions">
                    <button className="btn-icon" onClick={() => openEdit(contact)} title="Editar">
                      <Edit2 size={16} />
                    </button>
                    {!contact.customer && (
                      <button className="btn-icon" onClick={() => setConvertTarget({ contact, target: 'customer' })}
                        title="Convertir en cliente" style={{ color: '#16a34a' }}>
                        <UserPlus size={16} />
                      </button>
                    )}
                    {!contact.provider && (
                      <button className="btn-icon" onClick={() => setConvertTarget({ contact, target: 'provider' })}
                        title="Convertir en proveedor" style={{ color: '#1d4ed8' }}>
                        <Truck size={16} />
                      </button>
                    )}
                    <button className="btn-icon" onClick={() => setContactToDelete(contact.id)} title="Borrar" style={{ color: '#ef4444' }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Editar contacto' : 'Nuevo contacto'}</h2>
              <button className="btn-icon" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Nombre completo *</label>
                  <input className="form-input" value={form.contactName}
                    onChange={e => setForm({ ...form, contactName: e.target.value })} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Nombre</label>
                    <input className="form-input" value={form.firstName}
                      onChange={e => setForm({ ...form, firstName: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Apellidos</label>
                    <input className="form-input" value={form.lastName}
                      onChange={e => setForm({ ...form, lastName: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Teléfono</label>
                    <input className="form-input" value={form.phoneNumber}
                      onChange={e => setForm({ ...form, phoneNumber: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-input" value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Empresa</label>
                    <input className="form-input" value={form.companyName}
                      onChange={e => setForm({ ...form, companyName: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tipo</label>
                    <select className="form-input" value={form.phoneType}
                      onChange={e => setForm({ ...form, phoneType: e.target.value })}>
                      {PHONE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notas</label>
                  <textarea className="form-input" value={form.notes}
                    onChange={e => setForm({ ...form, notes: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Instrucciones especiales (las usa Minerva)</label>
                  <textarea className="form-input" rows={3} value={form.specialInstructions}
                    onChange={e => setForm({ ...form, specialInstructions: e.target.value })} />
                </div>
                {editing && (
                  <OwnerSelect
                    value={form.ownerId}
                    onChange={(id) => setForm({ ...form, ownerId: id })}
                  />
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar cambios' : 'Crear contacto'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {contactToDelete !== null && (
        <div className="modal-overlay" onClick={() => setContactToDelete(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar borrado</h2>
              <button className="btn-icon" onClick={() => setContactToDelete(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>¿Borrar este contacto de la agenda? Esta acción no se puede deshacer.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline" onClick={() => setContactToDelete(null)}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={() => handleDelete(contactToDelete)}>Borrar</button>
            </div>
          </div>
        </div>
      )}

      {convertTarget !== null && (
        <div className="modal-overlay" onClick={() => setConvertTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar conversión</h2>
              <button className="btn-icon" onClick={() => setConvertTarget(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>
                Convertir <strong>{convertTarget.contact.contactName}</strong> en {convertTarget.target === 'customer' ? 'cliente' : 'proveedor'}.
                Se creará un nuevo registro y quedará vinculado a este contacto.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline" onClick={() => setConvertTarget(null)}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={handleConvert}>Convertir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Phonebook;
